import { formatCustomDate } from "helpers/dateTools"
import { inventoryStatus } from "helpers/dictionary"
import { useSearch } from "hooks/useSearch"
import { useEffect, useRef, useState } from "react"
import { PROYECT, api_get } from "services/api"

const DEBOUNCE_DELAY = 1500

export const INVENTORY_CLOSED_MESSAGE = "El inventario está cerrado, por lo que no es posible registrar nuevos movimientos."

export const ROUTE_INVENTORY_CLOSED_MESSAGE = "Esta ruta ya cuenta con el inventario cerrado."

export const OPERATION_INVENTORY_CLOSED_MESSAGE = "El inventario se encuentra cerrado, por lo que no se permite dicha operación."

export const LIQUIDATION_INVENTORY_CLOSED_MESSAGE = "El inventario ya se encuentra cerrado, no se puede generar nuevas liquidaciones"

// Al cambiar la fecha se afecta también el inventario del que sale la liquidación
export const LIQUIDATION_ORIGIN_INVENTORY_CLOSED_MESSAGE = "El inventario de la fecha actual ya se encuentra cerrado, no se puede mover esta liquidación"

const inventoryStatusUrl = (id, date) =>
  `${process.env.REACT_APP_URL}${PROYECT}/station-inventory/${id}/is-open?date=${date}`

/**
 * Consulta si el inventario de una planta está abierto en una fecha.
 * Pensado para validaciones por acción del usuario (click), donde no aplica
 * el debounce del hook.
 * @param {string} id id de la planta
 * @param {string} date fecha en formato YYYY-MM-DD
 * @returns {Promise<boolean>} true si el inventario NO está cerrado
 */
export const checkInventoryIsOpen = async (id, date) => {
  const res = await api_get(inventoryStatusUrl(id, date))
  return res?.status_inventory !== inventoryStatus.CLOSED
}

const neutralState = {
  loading: false,
  errorInv: false,
  isSuccess: false
}

// closedMessage permite que cada módulo muestre su propio texto de inventario cerrado
export const useCheckInventoryStatus = (id = '', date = formatCustomDate(new Date(), '-'), closedMessage = INVENTORY_CLOSED_MESSAGE) => {
  const [data, setData] = useState(neutralState)
  const { setSnackbar } = useSearch()
  const isMounted = useRef(false)
  const timerRef = useRef(null)
  // Identifica la petición vigente: cualquier respuesta con un id distinto
  // corresponde a una fecha anterior y se descarta.
  const requestIdRef = useRef(0)

  const fetch = async (requestId, stationId, currentDate) => {
    const isStale = () => !isMounted.current || requestIdRef.current !== requestId

    try {
      const isOpen = await checkInventoryIsOpen(stationId, currentDate)
      if (isStale()) return
      if (isOpen) {
        setData({
          loading: false,
          errorInv: false,
          isSuccess: true,
        })
        return
      }
      setData({
        loading: false,
        errorInv: true,
        isSuccess: false,
        message: closedMessage
      })
    } catch (err) {
      if (isStale()) return
      setData({
        loading: false,
        errorInv: true,
        isSuccess: false,
      })
      setSnackbar(`${err || 'Error al Consultar Status'}`, "danger", "tc")
    }
  }

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!id || !date) return

    // Cancela la validación anterior: el timer pendiente y, si ya salió la
    // petición, su respuesta queda invalidada al cambiar el requestId.
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    requestIdRef.current += 1
    const requestId = requestIdRef.current

    setData({ ...neutralState, loading: true })
    timerRef.current = setTimeout(() => {
      fetch(requestId, id, date)
    }, DEBOUNCE_DELAY)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [id, date])

  return data
}
